import { Component, Input } from '@angular/core';
import { FileUploadService } from '@services/file-upload.service';
import { AttachmentDetailDto } from '@artemis-software/wr-api';
import { NbToastrService } from '@nebular/theme';
import { Attachment, AttachmentUploadJob, FormControlContext, PendingAttachment } from '../attachment-utils';
import { firstValueFrom } from 'rxjs';

@Component({
  standalone: false,
  selector: 'wr-attachment-upload-section[formControlContext]',
  templateUrl: './attachment-upload-section.component.html',
  styleUrls: ['./attachment-upload-section.component.scss'],
})
export class AttachmentUploadSectionComponent {

  @Input()
  type: AttachmentDetailDto.TypeEnum = AttachmentDetailDto.TypeEnum.File;
  @Input()
  formControlContext!: FormControlContext;
  @Input()
  filterBuildingSectionId: string | null | undefined = undefined;

  private _attachments: Attachment[] = [];
  private uploadChain: Promise<void> = Promise.resolve();

  @Input()
  set attachments(attachments: Attachment[] | undefined) {
    this._attachments = attachments ? [...attachments] : [];
  }

  get attachments(): Attachment[] {
    return this._attachments;
  }

  constructor(
    private fileUploadService: FileUploadService,
    private nbToastrService: NbToastrService,
  ) {
  }

  upload(file: File): void {
    const split = file.name.split('.');

    const pendingAttachment: PendingAttachment = {
      type: this.getType(file.type),
      fileName: split[0],
      fileEnding: split[1] ?? '',
      rotationDegrees: 0,
      tags: [],
      created: new Date().toISOString(),
      comment: '',
      deleted: false,
      creatorId: '',
      relatedEntityId: undefined,
      buildingSectionId: this.filterBuildingSectionId ?? undefined,
    };

    const currentAttachments = this.attachments;
    const currentEventLister = this.formControlContext.attachmentsChangeEventListener;
    const newListener = this.formControlContext.newAttachmentsEventListener;
    const currentPendingJobs = this.formControlContext.pendingJobs$;

    const job: AttachmentUploadJob = { attachment: pendingAttachment };
    currentPendingJobs.next([...currentPendingJobs.value, job]);

    this.uploadChain = this.uploadChain.then(async () => {
      try {
        const response = await firstValueFrom(this.fileUploadService.uploadSingleFileNoProgress(file));
        const uploadedAttachment: Attachment = {
          ...pendingAttachment,
          s3key: response.key,
          presignedUrl: response.publicUrl,
        };
        currentAttachments.push(uploadedAttachment);
        currentEventLister?.emit(currentAttachments.filter(a => !!a.s3key));
        newListener?.emit([uploadedAttachment]);
      } finally {
        currentPendingJobs.next(currentPendingJobs.value.filter(j => j !== job));
      }
    });
  }

  cancelJob(job: AttachmentUploadJob): void {
    this.nbToastrService.warning('Upload abgebrochen', 'Upload abgebrochen');
    const pendingJobs = this.formControlContext?.pendingJobs$;
    pendingJobs?.next(pendingJobs?.value.filter(j => j !== job));
  }

  private getType(type: string): AttachmentDetailDto.TypeEnum {
    if (type.startsWith('image')) {
      return AttachmentDetailDto.TypeEnum.Image;
    }
    return AttachmentDetailDto.TypeEnum.File;
  }
}
